import React, { FC, useEffect, useMemo, useState } from 'react'
import s from './EditMessagePopup.module.sass'

import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { getDefaultField } from '@shared/lib/fields/getDefaultField'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { BuildTextarea } from '@shared/ui/Form/Build/BuildTextarea'
import { LoadingSpinner } from '@shared/ui/Feedback/LoadingSpinner'
import { InputHint } from '@shared/ui/Form/Single/InputHint'
import { useField } from '@shared/lib/fields/hooks/useField/useField'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { Title } from '@shared/ui/Typography/Title'
import { Button } from '@shared/ui/Actions/Button'
import { Modal } from '@shared/ui/Overlays/Modal'

import iconThumb from '@icons/icon-thumb.svg'

interface Props {
  type: 'review' | 'answer'
  data: {
    body: string
    like: boolean
  }
  className?: string
  onChange: (data: { is_like?: boolean; body?: boolean }) => void
  onSend: () => void
  onClose: () => void
  loading?: boolean
}

const EditMessagePopup: FC<Props> = ({ type, data, onChange, onSend, onClose, loading, className = '' }) => {
  const [rateError, setRateError] = useState(false)
  const { isMobile } = useClientResponsive()
  const [comment, setComment, validComment] = useField(
    getDefaultField('textarea', {
      name: 'edit',
      value: data.body,
      required: true,
      validations: {
        'min-symbols-50': 'Отзыв должен содержать более 50 символов',
        'max-symbols-1500': 'Отзыв должен содержать не более 1500 символов',
      },
      settings: {
        placeholder: 'Ваш отзыв',
        height: '135px',
        resize: true,
        label: '',
      },
    }),
  )

  const isLike = data.like === true
  const isDislike = data.like === false

  const handleClickRateDislike = () => {
    onChange({ is_like: data.like === false ? null : false })
    setRateError(false)
  }
  const handleClickRateLike = () => {
    onChange({ is_like: data.like === true ? null : true })
    setRateError(false)
  }

  useEffect(() => {
    // @ts-ignore
    onChange({ body: comment.value })
  }, [comment.value])

  const validRate = () => {
    return data.like !== null
  }

  const handleSubmit = () => {
    const isValidComment = validComment().error === ''
    const isValidRate = validRate()

    if (isValidComment && isValidRate) {
      onSend()
    } else {
      console.error('new review is invalid')
    }
  }

  const handleClose = () => onClose()

  const classRateLike = useMemo(() => {
    return isLike ? `${s['rate-like']} ${s['active']}` : s['rate-like']
  }, [isLike])

  const classRateDislike = useMemo(() => {
    return isDislike ? `${s['rate-dislike']} ${s['active']}` : s['rate-dislike']
  }, [isDislike])

  return (
    <ErrorBoundary>
      <Modal size={'M'} handleClose={handleClose} title={isMobile && 'Ваш отзыв'} className={s.modal}>
        <div className={`${s.wrapper} ${className}`} data-loading={loading}>
          <div className={s.header}>
            {!isMobile && (
              <Title tag='h3' sizes='h2 h4'>
                Ваш отзыв
              </Title>
            )}
            <div className={s['rate-group']}>
              <IconButton
                icon={iconThumb}
                mode='white-border'
                sizes='XL L'
                active={isDislike}
                onClick={handleClickRateDislike}
                className={classRateDislike}
              />
              <IconButton
                icon={iconThumb}
                mode='white-border'
                sizes='XL L'
                active={isLike}
                onClick={handleClickRateLike}
                className={classRateLike}
              />
              {rateError && (
                <InputHint
                  status='error'
                  text='Поставьте «Нравится» или «Не нравится»'
                  sizes={'M S'}
                  className={s.error}
                />
              )}
            </div>
          </div>
          <form className={s.inner}>
            {/*// @ts-ignore*/}
            <BuildTextarea field={comment} setField={setComment} className={s.textarea} />
            <Button sizes={'XL L'} mode={'black'} onClick={handleSubmit} className={s.button}>
              Сохранить
            </Button>
          </form>
        </div>
        <LoadingSpinner loading={loading} />
      </Modal>
    </ErrorBoundary>
  )
}

export default EditMessagePopup
